import { ConnectPayload, InputChangePayload, ServerMessage } from '@common/api';
import { INTERACTION_CONFIG } from '@common/config';
import { getPrimaryInteraction } from '@common/data/Interactions';
import { isContinuous, isMovementAction, MOVEMENT_ACTIONS } from '@common/input';
import { CommonServiceContainer } from '@common/services/CommonServiceContainer';
import { DestroyMarkedEntitiesSystem } from '@common/systems/DestroyMarkedEntitiesSystem';
import { FacingDirectionSystem } from '@common/systems/FacingDirectionSystem';
import { InputSyncSystem } from '@common/systems/InputSyncSystem';
import { KnockbackSystem } from '@common/systems/KnockbackSystem';
import { MovementStateSystem } from '@common/systems/MovementStateSystem';
import { MovementSystem } from '@common/systems/MovementSystem';
import { ResetEventsSystem } from '@common/systems/ResetEventsSystem';
import { TransformPropagationSystem } from '@common/systems/TransformPropagationSystem';
import { findClosestInteractable } from '@common/utils/InteractionUtils';
import { Application, Container, TextureStyle, Ticker } from 'pixi.js';
import { createApp, reactive } from 'vue';

import { AssetManager } from './AssetManager';
import ConnectionOverlay from './components/ConnectionOverlay.vue';
import DebugCoordinateOverlay from './components/DebugCoordinateOverlay.vue';
import DebugPanel from './components/DebugPanel.vue';
import DialoguePanel from './components/DialoguePanel.vue';
import EntityInspector from './components/EntityInspector.vue';
import ExternalInventoryOverlay from './components/ExternalInventoryOverlay.vue';
import HudOverlay from './components/HudOverlay.vue';
import InventoryOverlay from './components/InventoryOverlay.vue';
import { getActionForKey } from './keybindings';
import { runtimeUrls } from './runtimeUrls';
import { ClientServiceContainer } from './services/ClientServiceContainer';
import { ClientWorldService } from './services/ClientWorldService';
import { AnimationSystem } from './systems/AnimationSystem';
import { AnimationTransformSystem } from './systems/AnimationTransformSystem';
import { CameraFollowSystem } from './systems/CameraFollowSystem';
import { ClientCollisionSystem } from './systems/ClientCollisionSystem';
import { ClientFastMovementSystem } from './systems/ClientFastMovementSystem';
import { ClientMovementIntentSystem } from './systems/ClientMovementIntentSystem';
import { ClientPlayerMovementModeSystem } from './systems/ClientPlayerMovementModeSystem';
import { ClientPositionSyncSystem } from './systems/ClientPositionSyncSystem';
import { ClientWeaponSwingSpawnSystem } from './systems/ClientWeaponSwingSpawnSystem';
import { ClientWeaponSwingUpdateSystem } from './systems/ClientWeaponSwingUpdateSystem';
import { DebugChunkBoundariesRenderSystem } from './systems/DebugChunkBoundariesRenderSystem';
import { DebugCollisionRenderSystem } from './systems/DebugCollisionRenderSystem';
import { DebugDestinationRenderSystem } from './systems/DebugDestinationRenderSystem';
import { DebugGridLinesRenderSystem } from './systems/DebugGridLinesRenderSystem';
import { DebugHitboxRenderSystem } from './systems/DebugHitboxRenderSystem';
import { DebugLineOfSightRenderSystem } from './systems/DebugLineOfSightRenderSystem';
import { DebugPathRenderSystem } from './systems/DebugPathRenderSystem';
import { DebugWeaponSwingRenderSystem } from './systems/DebugWeaponSwingRenderSystem';
import { DirectionalAnimationSystem } from './systems/DirectionalAnimationSystem';
import { EmoticonSystem } from './systems/EmoticonSystem';
import { EntityClickDebugSystem } from './systems/EntityClickDebugSystem';
import { ExternalUIAggroSystem } from './systems/ExternalUIAggroSystem';
import { FaceDecorationSystem } from './systems/FaceDecorationSystem';
import { FacingScaleSystem } from './systems/FacingScaleSystem';
import { FootstepParticleEmitSystem } from './systems/FootstepParticleEmitSystem';
import { FootstepParticleLoadingSystem } from './systems/FootstepParticleLoadingSystem';
import { FootstepParticleRenderSystem } from './systems/FootstepParticleRenderSystem';
import { FootstepSoundSystem } from './systems/FootstepSoundSystem';
import { InteractionHintRenderSystem } from './systems/InteractionHintRenderSystem';
import { InteractionUISyncSystem } from './systems/InteractionUISyncSystem';
import { LightingRenderSystem } from './systems/LightingRenderSystem';
import { MapRenderSystem } from './systems/MapRenderSystem';
import { NameRenderSystem } from './systems/NameRenderSystem';
import { RenderSystem } from './systems/RenderSystem';
import { SceneChangeDetectionSystem } from './systems/SceneChangeDetectionSystem';
import { SceneTransitionSystem } from './systems/SceneTransitionSystem';
import { ShadowRenderSystem } from './systems/ShadowRenderSystem';
import { SkinColourSystem } from './systems/SkinColourSystem';
import { SmoothingSystem } from './systems/SmoothingSystem';
import { SoundSystem } from './systems/SoundSystem';
import { SpriteLifecycleSystem } from './systems/SpriteLifecycleSystem';
import { SpriteLoadingSystem } from './systems/SpriteLoadingSystem';
import { SpritesheetResolverSystem } from './systems/SpritesheetResolverSystem';
import { TimeDisplaySystem } from './systems/TimeDisplaySystem';
import { AuthError, connectWebSocket } from './websockets';

/**
 * Extracts the game ID from the current URL path.
 * Expected format: /game/<gameId>
 */
function getGameIdFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/game\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Creates a new game via the API and navigates to it.
 * This function never returns normally - it either navigates away or throws.
 */
async function createNewGameAndNavigate(): Promise<never> {
  console.log('No game ID found in URL, creating new game...');

  let response: Response;

  try {
    response = await fetch(runtimeUrls.getGameCreationUrl().href, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Failed to create new game:', error);

    // Show user-friendly error - the page will need to be refreshed manually
    document.body.innerHTML = `
      <div style="padding: 2rem; font-family: sans-serif; text-align: center;">
        <h1>Failed to create game</h1>
        <p>Unable to connect to the game server. Please try again later.</p>
        <button onclick="location.reload()">Retry</button>
      </div>
    `;

    throw new Error('Game creation flow complete');
  }

  if (response.status === 403) {
    showNoAccessMessage(runtimeUrls.getAccountUrl());
    throw new Error('Game creation forbidden - not whitelisted');
  }

  try {
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    const { gameId } = await response.json();
    console.log(`Created new game: ${gameId}, navigating...`);
    window.location.href = `/game/${gameId}`;
  } catch (error) {
    console.error('Failed to create new game:', error);

    // Show user-friendly error - the page will need to be refreshed manually
    document.body.innerHTML = `
      <div style="padding: 2rem; font-family: sans-serif; text-align: center;">
        <h1>Failed to create game</h1>
        <p>Unable to connect to the game server. Please try again later.</p>
        <button onclick="location.reload()">Retry</button>
      </div>
    `;

    throw new Error('Game creation flow complete');
  }

  // Halt further module execution after navigation or error display
  throw new Error('Game creation flow complete');
}

const gameId = getGameIdFromUrl();

if (!gameId) {
  try {
    await createNewGameAndNavigate();
  } catch (error) {
    if (error instanceof Error && error.message.includes('forbidden')) {
      // Message already shown by showNoAccessMessage()
      throw error;
    }
    throw error;
  }
}

const activeGameId =
  gameId ??
  (() => {
    throw new Error('Game ID missing after client bootstrap');
  })();

/**
 * Renders a "No Access" message to the user.
 */
function showNoAccessMessage(accountUrl: string) {
  document.body.innerHTML = `
    <div style="
      height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #222;
      color: #eee;
      font-family: 'Outfit', 'Inter', sans-serif;
      text-align: center;
      padding: 2rem;
    ">
      <div style="
        background: rgba(40, 40, 40, 0.8);
        padding: 3rem;
        border-radius: 1rem;
        box-shadow: 0 10px 30px rgba(0,0,0,0.5);
        border: 1px solid #444;
        max-width: 500px;
      ">
        <h1 style="font-size: 2.5rem; margin-bottom: 1rem; color: #ff4d4d;">Access Denied</h1>
        <p style="font-size: 1.2rem; margin-bottom: 2rem; line-height: 1.6; color: #ccc;">
          This is a private demo. You must be whitelisted to play.
        </p>
        <a href="${accountUrl}" style="
          display: inline-block;
          background: #4a90e2;
          color: white;
          padding: 1rem 2.5rem;
          border-radius: 2rem;
          text-decoration: none;
          font-weight: bold;
          font-size: 1.1rem;
          transition: transform 0.2s, background 0.2s;
        " onmouseover="this.style.background='#357abd'; this.style.transform='scale(1.05)'"
           onmouseout="this.style.background='#4a90e2'; this.style.transform='scale(1)'">
          Log In to Check Access
        </a>
      </div>
    </div>
  `;
}

export const serverMessages: ServerMessage[] = [];

TextureStyle.defaultOptions.scaleMode = 'nearest';

// Initialize AssetManager
await AssetManager.getInstance().loadAssets();

const app = new Application();
const worldContainer = new Container();
worldContainer.interactive = true;
worldContainer.sortableChildren = true;
app.stage.addChild(worldContainer);

// Create a container for names that sits on top of all sprites
const namesContainer = new Container();
namesContainer.zIndex = 999999; // Always on top
worldContainer.addChild(namesContainer);

// Initialize services using dependency injection
const clientWorldService = new ClientWorldService();
const world = clientWorldService.getWorld();

// Create service containers
const commonServices = new CommonServiceContainer(clientWorldService.getWorld(), true);
const services = new ClientServiceContainer(commonServices, clientWorldService);

// Initialize particle container
services.footstepParticle.setStage(worldContainer);

// Pre-load common effect spritesheets and sounds
await services.emoticon.loadAll();
try {
  await services.spatialSound.loadSound('sound/footstep.ogg');
} catch (error) {
  // We log but don't block startup, as sounds can retry loading on-demand
  console.error('Failed to preload footstep sound:', error);
}

// Apply Vue reactivity to the debug state so the UI can monitor it
// without tying the DebugService class itself to Vue. This decoupling
// allows the service to remain framework-agnostic while still enabling
// reactive UI updates.
services.debug.state = reactive(services.debug.state);
services.entityInspector.state = reactive(services.entityInspector.state);

// Initialize RenderService with PixiJS references
services.render.initialize(app, worldContainer);

// Initialize the application
await app.init({ background: '#444', resizeTo: window, roundPixels: true });

// Initialize camera service with screen dimensions
services.camera.updateScreenSize(app.screen.width, app.screen.height);

// Update camera service on window resize
// Use window dimensions directly since PixiJS `resizeTo: window` means they'll match,
// and app.screen may not be updated yet when this event fires (e.g., on maximize/restore)
window.addEventListener('resize', () => {
  services.camera.updateScreenSize(window.innerWidth, window.innerHeight);
});

// Reference to sendMessage function, set after WebSocket connects
let sendInputMessage: ((payload: InputChangePayload) => void) | null = null;

const inputStartTimes: Partial<Record<string, number>> = {};

window.addEventListener('keydown', (event: KeyboardEvent) => {
  if (event.repeat) return;

  // Debug toggles are now handled by the Vue DebugPanel component

  const action = getActionForKey(event.code);
  const localPlayerService = services.localPlayer;
  if (action !== undefined && localPlayerService.playerId !== undefined) {
    // Block movement input when UI is open (dialogue, inventory, etc.)
    if (services.inputBlocking.isMovementBlocked() && isMovementAction(action)) {
      return;
    }
    // Block interact when inventory is open (prevent starting dialogue)
    if (services.inventoryUI.state.isOpen && action === 'interact') {
      return;
    }

    if (action === 'interact' && localPlayerService.entityId !== undefined) {
      const closestId = findClosestInteractable(
        world,
        localPlayerService.entityId,
        INTERACTION_CONFIG.EXECUTE_RADIUS,
        ['Interactable', 'Transform'],
      );

      if (closestId !== null) {
        // Logic check: only process if there is a valid interaction
        const interaction = getPrimaryInteraction(world, localPlayerService.entityId, closestId);
        if (interaction) {
          // Special case: Loot and Pickpocket open UI locally
          if (interaction.id === 'loot' || interaction.id === 'pickpocket') {
            services.inventoryUI.openLooting(closestId);
            // We NO LONGER return here - we want the interact message to go to the server
            // so it can set the InteractingWith component for leashing/monitoring.
          }
        } else {
          // No valid interaction, ignore keypress
          return;
        }
      }
    }

    // Track start time for this action
    inputStartTimes[action] = performance.now();
    services.common.input.setInput(localPlayerService.playerId, action, true);
    // Send to server immediately
    if (sendInputMessage) {
      sendInputMessage({ type: 'inputChange', action, value: true });
    }
  }
});

window.addEventListener('keyup', (event: KeyboardEvent) => {
  if (event.repeat) return;

  const action = getActionForKey(event.code);
  // Only handle keyup for continuous actions
  const playerId = services.localPlayer.playerId;
  if (action !== undefined && isContinuous(action) && playerId !== undefined) {
    // Skip processing keyup for movement when blocked
    // (we already cleared inputs when blocking started)
    if (services.inputBlocking.isMovementBlocked() && isMovementAction(action)) {
      return;
    }
    services.common.input.setInput(playerId, action, false);

    // Calculate duration
    let duration = 0;
    const startTime = inputStartTimes[action];
    if (startTime !== undefined) {
      duration = performance.now() - startTime;
      delete inputStartTimes[action];
    }

    // Send to server immediately
    if (sendInputMessage) {
      sendInputMessage({ type: 'inputChange', action, value: false, duration });
    }
  }
});

/**
 * Clears all currently held movement inputs and notifies the server.
 * This is necessary because some browser events (like 'blur' or opening the 'contextmenu')
 * prevent 'keyup' events from firing, which would otherwise leave the player moving indefinitely.
 */
function clearHeldInputs() {
  const playerId = services.localPlayer.playerId;
  if (playerId !== undefined) {
    for (const action of MOVEMENT_ACTIONS) {
      const startTime = inputStartTimes[action];
      if (startTime === undefined) {
        continue;
      }

      services.common.input.setInput(playerId, action, false);

      // Calculate duration
      const duration = performance.now() - startTime;
      delete inputStartTimes[action];

      // Send to server immediately
      if (sendInputMessage) {
        sendInputMessage({ type: 'inputChange', action, value: false, duration });
      }
    }
  }
}

window.addEventListener('blur', clearHeldInputs);
window.addEventListener('contextmenu', clearHeldInputs);

// Append the application canvas to the document body
document.getElementById('pixi-container')!.appendChild(app.canvas);

// Initialize Vue UI
const debugApp = createApp(DebugPanel, {
  debugService: services.debug,
});
debugApp.mount('#ui-root');

// Mount EntityInspector
const inspectorApp = createApp(EntityInspector, {
  inspectorService: services.entityInspector,
  world: clientWorldService.getWorld(),
});
inspectorApp.provide('services', services);
inspectorApp.mount('#inspector-root');

// Mount DialoguePanel
const dialogueApp = createApp(DialoguePanel, {
  dialogueService: services.dialogue,
  services: services,
});
dialogueApp.mount('#dialogue-root');

// Mount InventoryOverlay (which wraps InventoryPanel)
const inventoryApp = createApp(InventoryOverlay, {
  inventoryService: services.inventoryUI,
  assetManager: AssetManager.getInstance(),
  services: services,
});
inventoryApp.mount('#inventory-root');

// Mount ExternalInventoryOverlay (shared for Looting and Pickpocketing)
const externalInventoryApp = createApp(ExternalInventoryOverlay, {
  inventoryService: services.inventoryUI,
  assetManager: AssetManager.getInstance(),
  services: services,
});
externalInventoryApp.mount('#looting-root');

// Mount ConnectionOverlay for disconnection/reconnection feedback
const connectionOverlayApp = createApp(ConnectionOverlay, {
  connectionState: services.connectionState,
});
connectionOverlayApp.mount('#connection-overlay-root');

// Mount HudOverlay
const hudApp = createApp(HudOverlay);
hudApp.provide('services', services);
hudApp.mount('#hud-root');

// Mount DebugCoordinateOverlay
const debugOverlayApp = createApp(DebugCoordinateOverlay, {
  services: services,
});
debugOverlayApp.mount('#debug-overlay-root');

const nameRenderSystem = new NameRenderSystem(services);
nameRenderSystem.setNamesContainer(namesContainer);

// TODO: MapRenderSystem holds render state that needs resetting on reconnection.
// This extraction is a stopgap - ideally reconnection logic should live in a
// ClientGameInstance that owns all game-level lifecycle (mirroring server's GameInstance).
const mapRenderSystem = new MapRenderSystem(services);

const interactionHintRenderSystem = new InteractionHintRenderSystem(services);
interactionHintRenderSystem.setHintsContainer(namesContainer);

// Initialize EmoticonService container
services.emoticon.setContainer(namesContainer);

const emoticonSystem = new EmoticonSystem(services);

const clientSystems = [
  new InputSyncSystem(services.common),
  // Update facing direction immediately from input (intent-based, doesn't need velocity)
  new FacingDirectionSystem(services.common),
  new SceneChangeDetectionSystem(services),
  new SceneTransitionSystem(services),
  new SpriteLifecycleSystem(services),
  new SpriteLoadingSystem(services), // Ensure spritesheets are loaded
  new ClientWeaponSwingSpawnSystem(services),
  new ClientWeaponSwingUpdateSystem(services),
  mapRenderSystem,
  new TransformPropagationSystem(services.common), // Must run before SmoothingSystem
  new SmoothingSystem(services),
  new ClientPlayerMovementModeSystem(services),
  new ClientMovementIntentSystem(services),
  new KnockbackSystem(services.common),
  new MovementSystem(services.common),
  new ClientCollisionSystem(services),
  new ClientFastMovementSystem(services),
  // Movement state after collision resolves actual velocity
  new MovementStateSystem(services.common),
  // Sync confirmed position to server
  new ClientPositionSyncSystem(services),
  // Apply visual mirroring based on facing
  new FacingScaleSystem(services),
  // Animation selection system (handles both idle and walking)
  new DirectionalAnimationSystem(services),
  // Animation processing
  new AnimationSystem(services),
  // Apply per-frame transform modifiers (bounce/sway effects)
  new AnimationTransformSystem(services),
  new FaceDecorationSystem(services),
  new FootstepParticleLoadingSystem(services),
  new FootstepParticleEmitSystem(services),
  new FootstepSoundSystem(services),

  // Resolve spritesheet + frame to texture ID
  new SpritesheetResolverSystem(services),
  // Apply skin colour tint
  new SkinColourSystem(services),
  // Render shadows under moving entities and ground items
  new ShadowRenderSystem(services),
  new RenderSystem(services),
  new FootstepParticleRenderSystem(services),
  new CameraFollowSystem(services), // After SmoothingSystem updates VisualPosition
  new LightingRenderSystem(services),
  nameRenderSystem,
  interactionHintRenderSystem,
  new DebugDestinationRenderSystem(services),
  new DebugPathRenderSystem(services),
  new DebugGridLinesRenderSystem(services),
  new DebugChunkBoundariesRenderSystem(services),
  new DebugCollisionRenderSystem(services),
  new DebugLineOfSightRenderSystem(services),
  new DebugWeaponSwingRenderSystem(services),
  new DebugHitboxRenderSystem(services),
  new EntityClickDebugSystem(services),
  new DestroyMarkedEntitiesSystem(services.common),
  new SoundSystem(services),
  new ExternalUIAggroSystem(services),
  emoticonSystem,
  new TimeDisplaySystem(services),
  new InteractionUISyncSystem(services),
  new ResetEventsSystem(services.common),
];

let websocketController: ReturnType<typeof connectWebSocket> extends Promise<infer T> ? T : never;
try {
  websocketController = await connectWebSocket(
    runtimeUrls.getGameSessionUrl(activeGameId),
    runtimeUrls.getGameWebSocketUrl(activeGameId),
    {
      onOpen,
      onMessage,
      onClose,
      onError,
    },
  );
} catch (error) {
  if (error instanceof AuthError && error.status === 403) {
    showNoAccessMessage(runtimeUrls.getAccountUrl());
    throw new Error('WebSocket connection forbidden - not whitelisted');
  }
  throw error;
}

const { sendMessage } = websocketController;

// Wire up ConnectionService for systems that need to send messages
services.connection.sendMessage = sendMessage;

// Set up immediate input sending
sendInputMessage = (payload: InputChangePayload) => {
  const playerId = services.localPlayer.playerId;
  if (playerId !== undefined) {
    sendMessage({ playerId, payload: [payload] });
  }
};

// Set up dialogue callbacks
services.dialogue.setCallbacks(
  (choiceIndex: number) => {
    const playerId = services.localPlayer.playerId;
    if (playerId !== undefined) {
      sendMessage({ playerId, payload: [{ type: 'dialogueChoice', choiceIndex }] });
    }
  },
  () => {
    const playerId = services.localPlayer.playerId;
    if (playerId !== undefined) {
      sendMessage({ playerId, payload: [{ type: 'dialogueContinue' }] });
    }
  },
  () => {
    const playerId = services.localPlayer.playerId;
    if (playerId !== undefined) {
      sendMessage({ playerId, payload: [{ type: 'dialogueExit' }] });
    }
  },
);

// Set up inventory move callback
services.inventoryUI.setMoveCallback((fromEntityId, fromSlot, toEntityId, toSlot, intent) => {
  const playerId = services.localPlayer.playerId;
  if (playerId !== undefined) {
    sendMessage({
      playerId,
      payload: [{ type: 'inventoryMove', fromEntityId, fromSlot, toEntityId, toSlot, intent }],
    });
  }
});

// Set up inventory drop callback
services.inventoryUI.setDropCallback((entityId, slotIndex) => {
  const playerId = services.localPlayer.playerId;
  if (playerId !== undefined) {
    sendMessage({ playerId, payload: [{ type: 'inventoryDrop', entityId, slotIndex }] });
  }
});

// Set up equipment callbacks
services.inventoryUI.setEquipFromInventoryCallback(
  (inventoryEntityId, inventorySlot, equipmentEntityId, equipSlot, intent) => {
    const playerId = services.localPlayer.playerId;
    if (playerId !== undefined) {
      sendMessage({
        playerId,
        payload: [
          {
            type: 'equipFromInventory',
            inventoryEntityId,
            inventorySlot,
            equipmentEntityId:
              equipmentEntityId !== inventoryEntityId ? equipmentEntityId : undefined,
            equipSlot,
            intent,
          },
        ],
      });
    }
  },
);

services.inventoryUI.setUnequipToInventoryCallback(
  (equipmentEntityId, equipSlot, inventoryEntityId, inventorySlot, intent) => {
    const playerId = services.localPlayer.playerId;
    if (playerId !== undefined) {
      sendMessage({
        playerId,
        payload: [
          {
            type: 'unequipToInventory',
            equipmentEntityId,
            equipSlot,
            inventoryEntityId:
              inventoryEntityId !== equipmentEntityId ? inventoryEntityId : undefined,
            inventorySlot,
            intent,
          },
        ],
      });
    }
  },
);

services.inventoryUI.setSwapEquipmentCallback((entityId, fromSlot, toSlot, intent) => {
  const playerId = services.localPlayer.playerId;
  if (playerId !== undefined) {
    sendMessage({
      playerId,
      payload: [{ type: 'swapEquipment', entityId, fromSlot, toSlot, intent }],
    });
  }
});

services.inventoryUI.setCrossEntitySwapEquipmentCallback(
  (sourceEntityId, sourceSlot, targetEntityId, targetSlot, intent) => {
    const playerId = services.localPlayer.playerId;
    if (playerId !== undefined) {
      sendMessage({
        playerId,
        payload: [
          {
            type: 'crossEntitySwapEquipment',
            sourceEntityId,
            sourceSlot,
            targetEntityId,
            targetSlot,
            intent,
          },
        ],
      });
    }
  },
);

// Clear held movement inputs when blocking becomes active (e.g., dialogue/inventory opens mid-walk)
services.inputBlocking.setOnBlockedCallback(() => {
  const playerId = services.localPlayer.playerId;
  if (playerId !== undefined) {
    for (const action of MOVEMENT_ACTIONS) {
      services.common.input.setInput(playerId, action, false);
      if (sendInputMessage) {
        sendInputMessage({ type: 'inputChange', action, value: false, duration: 0 });
      }
      delete inputStartTimes[action];
    }
  }
});

app.ticker.add((ticker: Ticker) => {
  const deltaTime = ticker.deltaMS / 1000;

  // Check for silent disconnect (no messages within timeout)
  services.connectionState.checkTimeout();

  // Always process messages - they update connection state
  processServerMessages();

  // Skip simulation if not connected to server
  if (!services.connectionState.isConnected) {
    return;
  }

  // Freeze input state at start of frame
  services.common.input.freezeForFrame();

  runClientSystems(deltaTime);

  // Consume single inputs at end of frame
  if (services.localPlayer.playerId !== undefined) {
    services.common.input.consumeSingleInputs(services.localPlayer.playerId);
  }
});

function runClientSystems(deltaTime: number) {
  for (const s of clientSystems) {
    s.run(deltaTime);
  }
}

function processServerMessages() {
  services.messageDispatch.processMessages(serverMessages);
}

// Handlers removed - moved to service registration

function onOpen(event: Event): void {
  console.log('WebSocket connection opened:', event);
  services.connectionState.onSocketOpen();

  // 1. Clear rendered sprites from the stage FIRST
  // (Order matters: we must detach containers before destroying them)
  const stage = services.render.stage;
  if (stage) {
    stage.removeChildren();
    // Re-add names container on top
    stage.addChild(namesContainer);
    services.footstepParticle.setStage(stage);
  }

  // 2. Clear stale state and reset systems
  const world = services.clientWorld.getWorld();
  world.resetWorld();
  services.pixiInstance.clear();

  // Reset map render system (re-attaches its container to stage)
  mapRenderSystem.reset();

  // Reset service references that point to old entities
  // Also reset playerId - server will re-assign via whoAmI on reconnect
  services.localPlayer.reset();
  services.footstepParticle.reset();
  services.localPlayer.playerId = undefined;
  services.camera.cameraEntityId = undefined;
  services.chunkData.reset();

  // Session management is now handled server-side via cookies
  // Just send a connect message and let the server handle player association
  const payload: ConnectPayload = {
    type: 'connect',
  };
  sendMessage({ payload: [payload] });
}
function onMessage(event: MessageEvent): void {
  services.connectionState.onMessageReceived();
  const message: ServerMessage = JSON.parse(event.data);
  serverMessages.push(message);
}
function onClose(event: CloseEvent): void {
  console.log('WebSocket connection closed:', event);
  services.connectionState.onSocketClose();
}
function onError(event: Event): void {
  console.error('WebSocket error occurred:', event);
}
